// AddEditGoalDialog.jsx - Dialog for Adding or Editing a Goal in Template KPI

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    TextField,
    Autocomplete,
    CircularProgress,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';

const CATEGORY_OPTIONS = [
    { value: 'Main', label: 'Main' },
    { value: 'Next Step', label: 'Next Step' },
    { value: 'Other', label: 'Other' }
];

// Weight mapping based on complexity
const COMPLEXITY_WEIGHT_MAP = {
    'lowest': 1,
    'low': 2,
    'medium': 3,
    'high': 4,
    'highest': 5
};

// Weight options for dropdown
const WEIGHT_OPTIONS = [
    { value: 1, label: 'Lowest' },
    { value: 2, label: 'Low' },
    { value: 3, label: 'Medium' },
    { value: 4, label: 'High' },
    { value: 5, label: 'Highest' }
];

const AddEditGoalDialog = ({ open, isEditMode, goal, templateId, onClose, onSubmit }) => {
    const dispatch = useDispatch();
    const allGoals = useSelector(selectors.selectAllGoals) || [];
    const currentTemplateGoals = useSelector(selectors.selectCurrentTemplateGoals) || [];
    const departments = useSelector(selectors.selectDepartments) || [];
    const loadingDropdowns = useSelector(selectors.selectGoalsLoading);

    const [formData, setFormData] = useState({
        goalId: '',
        category: 'Main',
        weight: ''
    });

    const [errors, setErrors] = useState({});

    // Load goals and departments dropdown when dialog opens
    useEffect(() => {
        if (open) {
            dispatch(actions.fetchAllGoals());
            dispatch(actions.fetchDepartments());
        }
    }, [open, dispatch]);

    // Initialize form data when editing
    useEffect(() => {
        if (open && isEditMode && goal) {
            setFormData({
                goalId: goal.goalId,
                category: goal.category || 'Main',
                weight: parseFloat(goal.weight) || ''
            });
        } else if (open && !isEditMode) {
            setFormData({
                goalId: '',
                category: 'Main',
                weight: ''
            });
        }
        setErrors({});
    }, [open, isEditMode, goal]);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.goalId) {
            newErrors.goalId = 'Goal is required';
        }

        if (!formData.category) {
            newErrors.category = 'Category is required';
        }

        if (formData.weight === '' || formData.weight === null) {
            newErrors.weight = 'Weight is required';
        } else {
            const weight = Number(formData.weight);
            if (isNaN(weight) || weight < 1 || weight > 5) {
                newErrors.weight = 'Please select a valid weight';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validateForm()) return;

        const submitData = {
            goalId: formData.goalId,
            category: formData.category,
            weight: Number(formData.weight)
        };

        onSubmit(submitData);
    };

    const selectedGoalObj = allGoals.find(g => g.id === formData.goalId);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        {isEditMode ? 'Edit Goal' : 'Add Goal'}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                    {/* Goal Selection */}
                    <Autocomplete
                        fullWidth
                        options={allGoals.filter(goalOption => 
                            isEditMode || !currentTemplateGoals.some(templateGoal => 
                                templateGoal.goalId === goalOption.id
                            )
                        )}
                        getOptionLabel={(option) => option.name || ''}
                        value={selectedGoalObj || null}
                        onChange={(event, newValue) => {
                            handleChange('goalId', newValue ? newValue.id : '');
                            // Auto-fill weight based on complexity when goal is selected
                            if (newValue && !isEditMode) {
                                const defaultWeight = COMPLEXITY_WEIGHT_MAP[newValue.complexity] || 3;
                                handleChange('weight', defaultWeight.toString());
                            }
                        }}
                        disabled={isEditMode || loadingDropdowns}
                        loading={loadingDropdowns}
                        size="small"
                        filterOptions={(options, { inputValue }) => {
                            if (!inputValue) return options;

                            const searchLower = inputValue.toLowerCase();

                            return options.filter(option => {
                                // Search in goal name
                                const nameMatch = option.name?.toLowerCase().includes(searchLower);

                                // Search in goal description
                                const descriptionMatch = option.description?.toLowerCase().includes(searchLower);

                                // Search in complexity
                                const complexityMatch = option.complexity?.toLowerCase().includes(searchLower);

                                // Search in department names
                                let departmentMatch = false;
                                if (option.departmentIds && option.departmentIds.length > 0) {
                                    departmentMatch = option.departmentIds.some(deptId => {
                                        const dept = departments.find(d => String(d.id) === String(deptId));
                                        return dept && dept.name?.toLowerCase().includes(searchLower);
                                    });
                                }

                                // Return true if any field matches
                                return nameMatch || descriptionMatch || complexityMatch || departmentMatch;
                            });
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Goal *"
                                placeholder="Select a goal"
                                error={Boolean(errors.goalId)}
                                helperText={errors.goalId || (isEditMode ? 'Goal cannot be changed when editing' : '')}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingDropdowns ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        renderOption={(props, option) => {
                            // Map departmentIds to department names
                            let departmentNames = 'All Departments';
                            if (option.departmentIds && option.departmentIds.length > 0) {
                                const deptNames = option.departmentIds
                                    .map(deptId => {
                                        const dept = departments.find(d => String(d.id) === String(deptId));
                                        return dept ? dept.name : null;
                                    })
                                    .filter(name => name !== null);

                                departmentNames = deptNames.length > 0 ? deptNames.join(', ') : 'All Departments';
                            }

                            return (
                                <Box component="li" {...props}>
                                    <Box sx={{ width: '100%' }}>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {option.name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                                            <Chip
                                                label={option.complexity}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.7rem',
                                                    textTransform: 'capitalize'
                                                }}
                                            />
                                            <Chip
                                                label={departmentNames}
                                                size="small"
                                                color="secondary"
                                                variant="outlined"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.7rem',
                                                    maxWidth: 200
                                                }}
                                            />
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" sx={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 1,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            mt: 0.5
                                        }}>
                                            {option.description || 'No description'}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        }}
                    />

                    {/* Show selected goal details */}
                    {selectedGoalObj && (
                        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                                Selected Goal Details:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, mt: 1 }}>
                                {selectedGoalObj.name}
                            </Typography>
                            {selectedGoalObj.description && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    {selectedGoalObj.description}
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                <Typography variant="caption">
                                    <strong>Complexity:</strong> {selectedGoalObj.complexity}
                                </Typography>
                                <Typography variant="caption">
                                    <strong>Department:</strong> {(() => {
                                        const deptId = selectedGoalObj.departmentId;
                                        if (!deptId || deptId === null || deptId === undefined) return 'All Departments';
                                        const dept = departments.find(d => d.id === deptId);
                                        return dept?.name || 'All Departments';
                                    })()}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Category Selection */}
                    <Autocomplete
                        fullWidth
                        options={CATEGORY_OPTIONS}
                        getOptionLabel={(option) => option.label}
                        value={CATEGORY_OPTIONS.find(opt => opt.value === formData.category) || null}
                        onChange={(event, newValue) => {
                            handleChange('category', newValue ? newValue.value : 'Main');
                        }}
                        size="small"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Category *"
                                placeholder="Select category"
                                error={Boolean(errors.category)}
                                helperText={errors.category}
                            />
                        )}
                    />

                    {/* Weight Selection */}
                    <FormControl fullWidth size="small" error={Boolean(errors.weight)}>
                        <InputLabel>Weight *</InputLabel>
                        <Select
                            value={formData.weight}
                            label="Weight *"
                            onChange={(e) => handleChange('weight', e.target.value)}
                        >
                            {WEIGHT_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                        <Typography variant="body2" sx={{ fontSize: '16px' }}>
                                            {option.label.split(' (')[0]}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                            score: {option.value}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.weight && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                {errors.weight}
                            </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                            Weight based on goal complexity (1-5). Auto-filled when goal selected.
                        </Typography>
                    </FormControl>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} variant="outlined" size="small">
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loadingDropdowns}
                    size="small"
                >
                    {isEditMode ? 'Update' : 'Add'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddEditGoalDialog;
